import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { omit } from 'ramda';
import debounce from 'lodash.debounce';

import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { KnmRemoveFromPlanModal } from 'CorePackage/MainModule/components/KnmRemoveFromPlanModal';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmErknmFormRender } from 'KnmErknmPackage/MainModule/components/KnmErknmFormRender';
import { KnmErpFormRender } from 'KnmErpPackage/MainModule/components/KnmErpFormRender';
import { KnmHeader } from 'KnmPackage/KnmHeaderModule/components/KnmHeader';
import { KnmPmFormRender } from 'KnmPmPackage/MainModule/components/KnmPmFormRender';
import { AddKnmModal } from 'PlanListPackage/MainModule/components/AddKnmModal';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { SignModal } from 'CorePackage/MainModule/components/SignModal';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { getUserRoleIsProsecutor, getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { fetchSignUpload } from 'APIPackage/KnmModule/services/knmSignApi';
import { AccessDeniedForTestModal } from 'CorePackage/MainModule/components/AccessDeniedForTestModal';
import { getAutosaveSettings } from 'CorePackage/MainModule/common/getAutosaveSettings';

import { IKnmParams } from '../../../APIPackage/KnmModule/interfaces/IKnmParams';
import {
  knmDraftSave,
  knmExcludeFromPlan,
  knmRemoveVisibleModal,
  knmSetFormRef,
  knmSignSubmitFail,
} from '../actions/knmActions';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { useKnmFormErrorsInit } from '../hooks/useKnmFormErrorsInit';
import { useKnmFormRender } from '../hooks/useKnmFormRender';
import { IKnmForm } from '../interfaces/IKnmForm';
import { IKnmFormRef } from '../interfaces/IKnmFormRef';
import {
  getKnmAccessSettingsFormRef,
  getKnmData,
  getKnmDataErknmApproveApproved,
  getKnmDataLateChangesFieldLinks,
  getKnmDataNote,
  getKnmErknmProfKindsSelectOptions,
  getKnmExcludeDates,
  getKnmFormRef,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusDecisionAppealed,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErknmStatusRejected,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNew,
  getKnmIsWatchOnly,
  getKnmLoading,
  getKnmModalIsVisible,
  getKnmPlanFindStatus,
  getKnmStartDateIsCurrentYear,
  getKnmSystemType,
  getPunishmentNature,
} from '../selectors/knmSelectors';
import { ChangeStatusModal } from './ChangeStatusModal';
import { SignatureImportModal } from './SignatureImportModal';
import { FormLeavingGuard } from './FormLeavingGuard';
import styles from './KnmForm.module.scss';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { knmFormValidator } from '../common/knmFormValidator';
import { KnmFormValidator } from '../types/KnmFormValidator';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { adminGetFetchSettings } from '../../../PersonalAccountPackage/AdminModule/actions/adminAction';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  getDictionariesKnoSupervisionParentUuidData,
  getKnoSupervisionStartDate,
  hasAnyDictionaryFailed,
} from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import {
  AccessSettingsModal,
  IAccessSettingsFormRef,
} from '../../../KnmPmPackage/MainModule/components/AccessSettingsModal';
import { DictionaryKnoOrganisationsModal } from '../../../CorePackage/MainModule/components/DictionaryKnoOrganisationsModal';
import { AccessRestrictionNoticeModal } from '../../../CorePackage/MainModule/components/AccessRestrictionNoticeModal';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IProfKind } from '../../../APIPackage/DictionariesModule/interfaces/IProfKind';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { accountOrganizationsWithDistrictGet } from '../../../CorePackage/AccountModule/actions/accountActions';
import { ErknmCopyModal } from './ErknmCopyModal';
import {
  getKnmErknmInitialFormValues,
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
} from '../../../KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import { ComplexCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/ComplexCatalogsNamesEnum';
import { mergeKnmParams } from '../common/mergeKnmParams';
import { KnmMassCancelHeader } from '../../KnmHeaderModule/components/KnmMassCancelHeader';
import { ErknmPrescribeCancelModal } from './ErknmPrescribeCancelModal/ErknmPrescribeCancelModal';
import { ErknmFinishRoPassportModal } from './ErknmFinsihRoPassportModal/ErknmFinishRoPassportModal';
import { PmOpCloseWarningModal } from './PmOpCloseWarningModal/PmOpCloseWarningModal';
import { ErknmActCancelModal } from './ErknmActCancelModal/ErknmActCancelModal';
import { hasAnyCatalogFailed } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { HAS_GET_DATA_ERROR } from '../../../CorePackage/MainModule/constants/notices';
import { ErknmSignAfterCancelModal } from './ErknmSignAfterCancelModal/ErknmSignAfterCancelModal';
import { setFieldSilent } from '../common/mutators';
import { useKnmFormSignHandlers } from '../hooks/useKnmFormSignHandlers';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

export interface IKnmFormProps {
  approveAddNotification: (value: boolean) => void;
  fetchKnmUpdate: (data: IKnmParams) => void;
  handleApplyModal: (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => void;
  handleCloseModal: () => void;
  handleSubmit: (values: IKnmForm) => void;
  knmData?: IKnm;
  setCanSubmit: (canSubmit: boolean) => void;
  visibleModal: boolean;
  convertFormValuesByTypeMap: Record<
    KnmSystemTypesEnum,
    (
      values: IKnmForm,
      knmData?: IKnm,
      isNeededGetDocumentsFromKnmData?: boolean,
      isDraft?: boolean,
    ) => IKnmParams | null
  >;
}

export type IKnmFormRenderProps = Omit<IKnmFormProps, 'handleSubmit'> & FormRenderProps<IKnmForm>;

interface IKnmFormRenderStoreProps extends KnmFormValidator {
  accessSettingsFormRef: IAccessSettingsFormRef | undefined;
  isAccessRestrictionNoticeModalVisible: boolean;
  isAccessDeniedForTestModalVisible: boolean;
  isAccessSettingsModalVisible: boolean;
  isApproveAddNotificationModalVisible: boolean;
  isChangeStatusModalVisible: boolean;
  isChangeStatusToApprove: boolean;
  isCopyModalVisible: boolean;
  isErknmCopyModalVisible: boolean;
  isKnmCancelledModalVisible: boolean;
  isKnmExcludeModalVisible: boolean;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusReadyForApprove: boolean;
  isNewKnm: boolean;
  isOrganisationsModalVisible: boolean;
  isSignModalVisible: boolean;
  isSignUploadModalVisible: boolean;
  isWatchOnly: boolean;
  knmFormRef: IKnmFormRef | undefined;
  knmSystemType: KnmSystemTypesEnum | undefined;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  settings: IAdminSetting[];
  lateChangesFieldLinks: Map<string, string>;
  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  isLoading: boolean;
  knmRawData: IKnm | undefined;
  isErknmPrescribeCancelVisible: boolean;
  isErknmFinishRoPassportVisible: boolean;
  isErknmPmOpCloseWarningVisible: boolean;
  isActCancelVisible: boolean;
  hasCatalogFailed: boolean;
  hasDictionaryFailed: boolean;
  isErknmSignAfterCancelVisible: boolean;
}

const KnmFormRender = ({
  knmData,
  form,
  values,
  errors,
  hasValidationErrors,
  submitFailed,
  touched,
  visited,
  visibleModal,
  handleSubmit,
  setCanSubmit,
  handleCloseModal,
  handleApplyModal,
  pmTypesSelectOptions,
  knmSystemType,
  isWatchOnly,
  settings,
  isCopyModalVisible,
  isChangeStatusModalVisible,
  isChangeStatusToApprove,
  isKnmStatusFillInProcess,
  isKnmStatusReadyForApprove,
  isSignModalVisible,
  isSignUploadModalVisible,
  isNewKnm,
  isAccessSettingsModalVisible,
  isOrganisationsModalVisible,
  isAccessRestrictionNoticeModalVisible,
  isAccessDeniedForTestModalVisible,
  accessSettingsFormRef,
  approveAddNotification,
  knmFormRef,
  isApproveAddNotificationModalVisible,
  isKnmCancelledModalVisible,
  isKnmExcludeModalVisible,
  isErknmCopyModalVisible,
  knoOrganizationsSelectOptions,
  isLoading,
  convertFormValuesByTypeMap,
  knmRawData,
  lateChangesFieldLinks,
  isErknmPrescribeCancelVisible,
  isErknmFinishRoPassportVisible,
  isErknmPmOpCloseWarningVisible,
  isActCancelVisible,
  hasCatalogFailed,
  hasDictionaryFailed,
  isErknmSignAfterCancelVisible,
}: IKnmFormRenderProps & IKnmFormRenderStoreProps) => {
  const isErpKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERP;
  const isErknmKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERKNM;
  const isPmKnmSystemType = knmSystemType === KnmSystemTypesEnum.PM;

  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setFormRef = useCallback((formRef: IKnmFormRef) => dispatch(knmSetFormRef(formRef)), [dispatch]);
  const excludeKnmFromPlan = useCallback(
    (params?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption) => dispatch(knmExcludeFromPlan(params)),
    [dispatch],
  );

  const hasAnyGetDataError = useMemo(() => {
    return hasCatalogFailed || hasDictionaryFailed;
  }, [hasCatalogFailed, hasDictionaryFailed]);

  useEffect(() => {
    dispatch(adminGetFetchSettings());
    dispatch(accountOrganizationsWithDistrictGet());
  }, [dispatch]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const formRef = useRef(form);
  const passportData = useRef<IKnmForm>();
  const isLoadingRef = useRef<boolean>();
  const saveDraftKnmRef = useRef<(data: IKnmForm) => void>();
  const autosaveSettings = getAutosaveSettings(settings);
  const autosaveEnabledRef = useRef(autosaveSettings.enable);
  const autosaveMinsRef = useRef(autosaveSettings.mins);
  const debouncedOnFormValuesChangeRef = useRef<(() => void) | undefined>();

  const autosaveDelay = autosaveSettings.mins * 60 * 1000;

  formRef.current = form;

  useEffect(() => {
    setFormRef(formRef);
  }, [setFormRef, formRef]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (autosaveEnabledRef.current !== autosaveSettings.enable) {
      autosaveEnabledRef.current = autosaveSettings.enable;
    }
  }, [autosaveSettings.enable]);

  useEffect(() => {
    if (autosaveMinsRef.current !== autosaveSettings.mins) {
      autosaveMinsRef.current = autosaveSettings.mins;
    }
  }, [autosaveSettings.mins]);

  const saveDraftKnm = useCallback(
    (data: IKnmForm) => {
      if (knmSystemType && knmRawData && !hasAnyGetDataError) {
        const knmRequestValuesForDraft = convertFormValuesByTypeMap[knmSystemType](data, knmRawData, false, true);

        if (knmRequestValuesForDraft) {
          dispatch(knmDraftSave(mergeKnmParams(knmRawData, knmRequestValuesForDraft)));
        }
      }
    },
    [convertFormValuesByTypeMap, dispatch, hasAnyGetDataError, knmRawData, knmSystemType],
  );

  useEffect(() => {
    saveDraftKnmRef.current = saveDraftKnm;
  }, [saveDraftKnm]);

  useEffect(() => {
    if (hasAnyGetDataError) {
      dispatch(
        addNoticeAction({
          renderContent: HAS_GET_DATA_ERROR,
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.HAS_GET_DATA_ERROR,
        }),
      );
    }
  }, [hasAnyGetDataError]);

  const compareDataAndSaveDraft = useCallback(() => {
    const formValues = formRef.current?.getState()?.values;

    if (!formValues) {
      return;
    }

    const currentPassportData = { ...formValues };
    const snapshotForCompare = { ...currentPassportData };

    delete snapshotForCompare.draft;
    delete snapshotForCompare.draftSavedAt;

    if (!passportData.current) {
      passportData.current = currentPassportData;

      return;
    }

    const passportDataOld = { ...passportData.current };

    delete passportDataOld.draft;
    delete passportDataOld.draftSavedAt;

    if (JSON.stringify(passportDataOld) !== JSON.stringify(snapshotForCompare)) {
      passportData.current = currentPassportData;
      saveDraftKnmRef.current?.(currentPassportData);
    }
  }, []);

  const onFormValuesChange = useCallback(() => {
    const isDirty = formRef.current?.getState()?.dirty;

    if (isDirty && !isLoadingRef.current && autosaveEnabledRef.current) {
      compareDataAndSaveDraft();
    }
  }, [compareDataAndSaveDraft]);

  useEffect(() => {
    if (autosaveDelay <= 0) {
      debouncedOnFormValuesChangeRef.current = undefined;

      return undefined;
    }

    const debouncedOnFormValuesChange = debounce(onFormValuesChange, autosaveDelay);

    debouncedOnFormValuesChangeRef.current = debouncedOnFormValuesChange;

    return () => {
      debouncedOnFormValuesChange.cancel();
    };
  }, [autosaveDelay, onFormValuesChange]);

  // Подписка на состояния формы для отслеживания изменений и запуска таймера
  useEffect(() => {
    if (!formRef?.current) return undefined;

    const unsubscribe = formRef.current.subscribe(
      () => {
        debouncedOnFormValuesChangeRef.current?.();
      },
      { values: true, dirty: true },
    );

    return () => {
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!isLoading && formRef.current) {
      passportData.current = formRef.current.getState().values;
    }
  }, [isLoading, knmData?.draft, knmData?.id]);

  const handleFormSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(e);
      hasValidationErrors && dispatch(knmSignSubmitFail(errors));
    },
    [dispatch, errors, handleSubmit, hasValidationErrors],
  );

  const handleClearDraftPassportData = useCallback(() => {
    passportData.current = undefined;
  }, []);

  useKnmFormErrorsInit({
    formRef,
    errors,
    submitFailed,
    touched,
    visited,
    knmSystemType,
    values,
    settings,
  });

  const {
    isRemoveFromPlanVisible,
    closeRemoveFromPlanModal,
    handleCopyModalApply,
    closeCopyModal,
    handleChangeStatusModalApply,
    closeChangeStatusModal,
    closeSignModal,
    closeSignUploadModal,
    handleSignUploadModalApply,
    handleCloseAccessSettingsModal,
    handleCloseOrganisationsModal,
    handleApplyOrganisationsModal,
    handleCloseErknmCopyModal,
    handleCloseErknmPrescribeCancelModal,
    handleCloseErknmActCancelModal,
    handleCloseErknmFinishRoPassportModal,
    handleClosePmOpCloseWarningModal,
    handleCloseSignAfterCancelModal,
  } = useKnmFormRender({
    formRef,
    isChangeStatusToApprove,
    setCanSubmit,
    accessSettingsFormRef,
    knmSystemType,
    handleClearDraftPassportData,
    knmData,
  });

  const isEnableKnoValidationSigning = useMemo(() => {
    const option = settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_KNO_VALIDATION_SIGNING);

    return option?.value === 'true';
  }, [settings]);

  const { handleSign, handleSignAfterCancelModal } = useKnmFormSignHandlers({
    knmData,
    settings,
    isKnmStatusFillInProcess,
    isKnmStatusReadyForApprove,
    values,
    knoOrganizationsSelectOptions,
    closeSignModal,
    knmSystemType,
    pmTypesSelectOptions,
    isEnableKnoValidationSigning,
    handleCloseSignAfterCancelModal,
  });

  /**
   * Закрытие модального окна отмены паспорта
   */
  const onCloseKnmCancelledModal = useCallback(() => {
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.KNM_CANCELLED_MODAL));
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.KNM_EXCLUDE_MODAL));
  }, [dispatch]);

  /**
   * Подтверждение отмены паспорта КНМ
   */
  const onApplyKnmCancelledModal = useCallback(() => {
    formRef?.current.change('acceptCancel', true);
    formRef?.current.submit();

    const { errors: currentErrors, hasValidationErrors: isValidationErrors } = formRef.current.getState();

    isValidationErrors && dispatch(knmSignSubmitFail(currentErrors));

    dispatch(knmRemoveVisibleModal(KnmModalsEnum.KNM_CANCELLED_MODAL));
    dispatch(knmRemoveVisibleModal(KnmModalsEnum.KNM_EXCLUDE_MODAL));
  }, [dispatch, formRef]);

  function renderForm(menu: ReactNode, content: ReactNode) {
    return (
      <>
        <form className={styles.Form} onSubmit={handleFormSubmit}>
          <KnmHeader values={values} isPm={isPmKnmSystemType} clearDraftPassportData={handleClearDraftPassportData} />
          <KnmMassCancelHeader />
          {menu}
          <div className={styles.Body} ref={bodyRef}>
            {content}
          </div>
        </form>

        <ConfirmModal
          visible={isCopyModalVisible}
          title="Копирование КНМ"
          text='При выполнении действия "Копировать" будет создано новое КНМ, предзаполненное данными проверки, из паспорта которой осуществлялось копирование. Копировать?'
          applyButtonText="Копировать"
          cancelButtonText="Отменить"
          onApply={handleCopyModalApply}
          onClose={closeCopyModal}
        />
        <ChangeStatusModal
          visible={isChangeStatusModalVisible}
          onApply={handleChangeStatusModalApply}
          onClose={closeChangeStatusModal}
        />
        <SignModal
          visible={isSignModalVisible}
          title={`Подписание паспорта ${knmSystemType === KnmSystemTypesEnum.PM ? 'ПМ' : 'КНМ'}`}
          onSign={handleSign}
          onClose={closeSignModal}
        />
        <SignatureImportModal
          visible={isSignUploadModalVisible}
          onSend={handleSignUploadModalApply}
          onClose={closeSignUploadModal}
          uploadFunc={fetchSignUpload}
          id={knmData?.id}
          isEnableKnoValidationSigning={isEnableKnoValidationSigning}
        />
        <KnmRemoveFromPlanModal
          visible={isRemoveFromPlanVisible}
          knmsNumber={1}
          isErknm={isErknmKnmSystemType}
          onApply={excludeKnmFromPlan}
          onClose={closeRemoveFromPlanModal}
        />
        <AddKnmModal
          visible={visibleModal}
          onApply={handleApplyModal}
          onClose={handleCloseModal}
          isErknm={isErknmKnmSystemType}
          isKnm
          isCreatePlan={values.isCreatePlan}
        />
        {!isWatchOnly && !isNewKnm && <FormLeavingGuard formRef={knmFormRef} />}
        <AccessSettingsModal
          visible={isAccessSettingsModalVisible}
          onClose={handleCloseAccessSettingsModal}
          knoOrganization={values.knoOrganizationPm || values.knoOrganizationErknm}
          collaboratingOrganizations={values.collaboratingOrganizationsErknm}
        />
        <DictionaryKnoOrganisationsModal
          visible={isOrganisationsModalVisible}
          onClose={handleCloseOrganisationsModal}
          onApply={handleApplyOrganisationsModal}
        />
        <AccessRestrictionNoticeModal visible={isAccessRestrictionNoticeModalVisible} />
        <AccessDeniedForTestModal visible={isAccessDeniedForTestModalVisible} />
        <ConfirmModal
          visible={isKnmCancelledModalVisible}
          title="Внимание!"
          text="Мероприятие будет переведено в статус «Отменено». Отмененные мероприятия будет невозможно восстановить. Если Вы уверены, что хотите отменить мероприятие, нажмите «Подтвердить». Если Вы не хотите отменять мероприятие, нажмите «Назад»"
          applyButtonText="Подтвердить"
          cancelButtonText="Назад"
          onApply={onApplyKnmCancelledModal}
          onClose={onCloseKnmCancelledModal}
        />
        <ConfirmModal
          visible={isKnmExcludeModalVisible}
          title="Внимание!"
          text="Мероприятие будет исключено из плана и переведено в статус «Исключена» с признаком «Проведен профилактический визит вместо КНМ». Если Вы уверены, что хотите продолжить, нажмите «Подтвердить». Если Вы не хотите исключать мероприятие из плана, нажмите «Назад»"
          applyButtonText="Подтвердить"
          cancelButtonText="Назад"
          onApply={onApplyKnmCancelledModal}
          onClose={onCloseKnmCancelledModal}
        />
        <ConfirmModal
          visible={isApproveAddNotificationModalVisible}
          title="Предупреждение"
          text="После сохранения паспорта изменение записи об уведомлении контролируемого лица будет недоступно. Продолжить?"
          applyButtonText="Подтвердить"
          cancelButtonText="Отмена"
          onApply={() => approveAddNotification(true)}
          onClose={() => approveAddNotification(false)}
        />
        <ErknmCopyModal visible={isErknmCopyModalVisible} onClose={handleCloseErknmCopyModal} />
        <ErknmPrescribeCancelModal
          isPm={isPmKnmSystemType}
          visible={isErknmPrescribeCancelVisible}
          onClose={handleCloseErknmPrescribeCancelModal}
        />
        <ErknmSignAfterCancelModal
          isDecision={false}
          isKnm
          isPm={isPmKnmSystemType}
          visible={isErknmSignAfterCancelVisible}
          onSign={handleSignAfterCancelModal}
          onClose={handleCloseSignAfterCancelModal}
        />
        <ErknmActCancelModal
          isPm={isPmKnmSystemType}
          visible={isActCancelVisible}
          onClose={handleCloseErknmActCancelModal}
        />
        <ErknmFinishRoPassportModal
          visible={isErknmFinishRoPassportVisible}
          onClose={handleCloseErknmFinishRoPassportModal}
        />
        <PmOpCloseWarningModal visible={isErknmPmOpCloseWarningVisible} onClose={handleClosePmOpCloseWarningModal} />
        {!isWatchOnly && <FormLeavingGuard formRef={knmFormRef} />}
      </>
    );
  }

  if (isErpKnmSystemType) {
    return (
      <KnmErpFormRender knmData={knmData} formRef={formRef} bodyRef={bodyRef} values={values} renderForm={renderForm} />
    );
  }

  if (isErknmKnmSystemType) {
    return (
      <KnmErknmFormRender
        knmData={knmData}
        formRef={formRef}
        bodyRef={bodyRef}
        values={values}
        renderForm={renderForm}
        lateChangesFieldLinks={lateChangesFieldLinks}
        touched={touched}
        settings={settings}
      />
    );
  }

  if (isPmKnmSystemType) {
    return (
      <KnmPmFormRender
        knmData={knmData}
        bodyRef={bodyRef}
        values={values}
        renderForm={renderForm}
        lateChangesFieldLinks={lateChangesFieldLinks}
        touched={touched}
        settings={settings}
      />
    );
  }

  return null;
};

const KnmFormWithoutStore = ({ handleSubmit, ...rest }: IKnmFormProps & IKnmFormRenderStoreProps) => {
  function renderForm(props: FormRenderProps<IKnmForm>) {
    return <KnmFormRender {...rest} {...props} />;
  }

  const validatorObjSchema = new KnmFormValidator();

  const validatorRest = omit(
    Object.keys(rest).filter(item => !(item in validatorObjSchema)),
    rest,
  ) as KnmFormValidator;

  return (
    <Form<IKnmForm>
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators, setFieldSilent }}
      render={renderForm}
      validate={knmFormValidator(validatorRest)}
    />
  );
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isPm: getKnmSystemType(state) === KnmSystemTypesEnum.PM,
  planFindStatus: getKnmPlanFindStatus(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isCopyModalVisible: getKnmModalIsVisible([KnmModalsEnum.COPY_KNM_MODAL])(state),
  isChangeStatusModalVisible: getKnmModalIsVisible([
    KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE,
    KnmModalsEnum.CHANGE_STATUS_MODAL_TO_REVISION,
  ])(state),
  isChangeStatusToApprove: getKnmModalIsVisible([KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE])(state),
  isSignModalVisible: getKnmModalIsVisible([KnmModalsEnum.SIGN_MODAL])(state),
  isSignUploadModalVisible: getKnmModalIsVisible([KnmModalsEnum.SIGN_UPLOAD_MODAL])(state),
  isApproveAddNotificationModalVisible: getKnmModalIsVisible([KnmModalsEnum.APPROVE_ADD_NOTIFICATION_MODAL])(state),
  isKnmCancelledModalVisible: getKnmModalIsVisible([KnmModalsEnum.KNM_CANCELLED_MODAL])(state),
  isKnmExcludeModalVisible: getKnmModalIsVisible([KnmModalsEnum.KNM_EXCLUDE_MODAL])(state),
  isErknmCopyModalVisible: getKnmModalIsVisible([KnmModalsEnum.ERKNM_COPY_MODAL])(state),
  isErknmPrescribeCancelVisible: getKnmModalIsVisible([KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL])(state),
  isActCancelVisible: getKnmModalIsVisible([KnmModalsEnum.ERKNM_ACT_CANCEL])(state),
  isErknmFinishRoPassportVisible: getKnmModalIsVisible([KnmModalsEnum.ERKNM_FINISH_RO_PASSPORT])(state),
  isErknmPmOpCloseWarningVisible: getKnmModalIsVisible([KnmModalsEnum.PM_OP_CANCEL_WARNING_PASSPORT])(state),
  isErknmSignAfterCancelVisible: getKnmModalIsVisible([KnmModalsEnum.ERKNM_SIGN_AFTER_CANCEL])(state),
  isNewKnm: getKnmIsNew(state) || false,
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusReadyForApprove: getKnmIsErknmStatusReadyForApprove(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeDecisionAppealed: getKnmIsErknmStatusDecisionAppealed(state),
  isKnmStatusTypeCantBeCarryOut: getKnmIsErknmStatusTypeCantBeCarryOut(state),
  isKnmStatusCommentsExist: getKnmIsErknmStatusCommentsExist(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  knmDataApprovedType: getKnmDataErknmApproveApproved(state),
  knmDataNote: getKnmDataNote(state),
  knmSystemType: getKnmSystemType(state),
  settings: getAdminFetchSettings(state),
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  knoSupervisionStartDate: getKnoSupervisionStartDate(state),
  isStartDateIsCurrentYear: getKnmStartDateIsCurrentYear(state),
  excludeDates: getKnmExcludeDates(state),
  isErknmStatusRejected: getKnmIsErknmStatusRejected(state),
  isAccessSettingsModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_SETTINGS])(state),
  isOrganisationsModalVisible: getKnmModalIsVisible([KnmModalsEnum.DICTIONARY_KNO_ORGANISATIONS])(state),
  isAccessRestrictionNoticeModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_RESTRICTION_NOTICE])(state),
  isAccessDeniedForTestModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_DENIED_FOR_TEST])(state),
  accessSettingsFormRef: getKnmAccessSettingsFormRef(state),
  pmTypesSelectOptions: getKnmErknmProfKindsSelectOptions(state),
  knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  punishmentNature: getPunishmentNature(state),
  knoSupervisionParentUuid: getDictionariesKnoSupervisionParentUuidData(state),
  knmFormRef: getKnmFormRef(state),
  initialErknmValues: getKnmErknmInitialFormValues(state),
  isPassportByCheckPrescriptionOldRules: getKnmErknmIsPrescriptionOldRules(state),
  isPassportByCheckPrescriptionFailed: getKnmErknmIsPrescriptionFailed(state),
  lateChangesFieldLinks: getKnmDataLateChangesFieldLinks(state),
  fnsRegistryOkvedCodes: state.catalogs[ComplexCatalogsNamesEnum.FNS_REGISTRY_OKVED_CODES],
  isLoading: getKnmLoading(state),
  knmRawData: getKnmData(state),
  hasCatalogFailed: hasAnyCatalogFailed(state),
  hasDictionaryFailed: hasAnyDictionaryFailed(state),
});

export const KnmForm = connect(mapStateToProps)(KnmFormWithoutStore);
